import React, { useCallback, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import HomeWallboardPanel from '../components/HomeWallboardPanel'
import KioskLobbyAttract from '../components/KioskLobbyAttract'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileAlt, faTools, faBook, faUserShield, faBriefcase, faCreditCard, faCar, faFilePdf, faShip, faCalculator, faUser, faUsers, faShieldAlt, faClipboardCheck, faCloudSun, faBullhorn, faGaugeHigh } from '@fortawesome/free-solid-svg-icons'
import { useSettings } from '../contexts/SettingsContext'
import { useAuth } from '../contexts/AuthContext'
import { canAccessTallyApp } from '../utils/tallyPermissions'
import { isKioskRole, useKioskRole } from '../hooks/useKioskRole'
import { runViewTransition } from '../utils/runViewTransition'

type HomeTab = 'internal' | 'external' | 'wallboard'

type AppCard = {
  title: string
  permissionKey: string | null
  icon: typeof faFileAlt
  description: string
  longDescription: string
  usage: string
  link: string
  color: string
}

const appCards: AppCard[] = [
  {
    title: 'Wallboard',
    permissionKey: null,
    icon: faCloudSun,
    description: 'Weather and at-a-glance info for the office or field.',
    longDescription: 'Wallboard shows multi-location weather and other snapshot information using portal defaults.',
    usage: 'Open Wallboard for a quick read on conditions across your configured locations.',
    link: '/',
    color: 'bg-sky-600 text-white',
  },
  {
    title: 'Forms',
    permissionKey: 'forms',
    icon: faFileAlt,
    description: 'Access and submit all company forms.',
    longDescription: 'The Forms app allows employees to access, fill out, and submit all required company forms digitally, streamlining paperwork and approvals.',
    usage: 'LTS uses Forms for timesheets, safety checklists, and HR documentation, ensuring compliance and efficiency.',
    link: '/forms',
    color: 'bg-primary text-primary-content',
  },
  {
    title: 'Projects',
    permissionKey: 'projects',
    icon: faTools,
    description: 'Manage sites and site templates.',
    longDescription: 'Projects provides tools for managing sites and site templates.',
    usage: 'LTS relies on Projects to coordinate field teams, monitor progress, and optimize resource allocation.',
    link: '/projects',
    color: 'bg-secondary text-secondary-content',
  },
  {
    title: 'Template',
    permissionKey: 'template',
    icon: faTools,
    description: 'Scaffold new portal apps using shared patterns.',
    longDescription: 'Template provides a starter structure for new apps inside the portal, including tabbed UI and management gating.',
    usage: 'Use Template as a starting point when building new apps that need consistent RBAC and centralized API workflows.',
    link: '/template',
    // Use a solid theme color (no opacity) so the card stays visible in all themes.
    color: 'bg-[#FF7F50] text-white',
  },
  {
    title: 'PR',
    permissionKey: 'pr',
    icon: faBullhorn,
    description: 'Internal PR tools and workflows.',
    longDescription:
      'PR is an internal app scaffold for communications-related workflows. It uses the same permission patterns as other portal apps.',
    usage: 'Use PR for features that should be gated separately from other internal apps.',
    link: '/pr',
    color: 'bg-accent text-accent-content',
  },
  {
    title: 'Resources',
    permissionKey: 'resources',
    icon: faBook,
    description: 'Find company resources and documentation.',
    longDescription: 'Resources is a central library for company policies, manuals, and training materials.',
    usage: 'LTS employees access Resources for up-to-date documentation and learning materials.',
    link: '/resources',
    color: 'bg-info text-info-content',
  },
  {
    title: 'Control Center',
    permissionKey: 'controlCenter',
    icon: faGaugeHigh,
    description: 'Operations overview and control tools.',
    longDescription:
      'Control Center brings operational status and management tools into one place for day-to-day portal control.',
    usage: 'Use Control Center for operational visibility and related management workflows.',
    link: '/control-center',
    color: 'bg-slate-700 text-white',
  },
  {
    title: 'Tally',
    permissionKey: 'tally',
    icon: faClipboardCheck, // Use a relevant icon
    description: 'Track equipment and inventory.',
    longDescription: 'Tally is the inventory and asset management app for LTS. It helps you track company equipment and inventory in real time.',
    usage: 'LTS uses Tally to manage assets and keep inventory up to date.',
    link: '/tally',
    color: 'bg-pink-600 text-white',
  },
  {
    title: 'Admin',
    permissionKey: 'adminPage',
    icon: faUserShield,
    description: 'Admin tools and user management.',
    longDescription: 'Admin provides access to user management, permissions, and system settings.',
    usage: 'LTS admins use this app to onboard users, assign roles, and configure system preferences.',
    link: '/admin',
    color: 'bg-warning text-warning-content',
  },
  {
    title: 'HR',
    permissionKey: 'hrManager',
    icon: faUsers,
    description: 'HR manager tools and workflows.',
    longDescription: 'HR is the dedicated space for HR manager workflows and employee profile administration.',
    usage: 'Use HR for manager-only employee search, record edits, and certification management.',
    link: '/hr',
    color: 'bg-indigo-600 text-white',
  },
  {
    title: 'Profile',
    permissionKey: 'profilePage',
    icon: faUser,
    description: 'Manage your HR file and make requests.',
    longDescription: 'Profile allows you to view and manage your HR file.',
    usage: 'LTS employees use Profile to keep their information current and manage their account preferences.',
    link: '/profile',
    color: 'bg-success text-success-content',
  },
  {
    title: 'JobTread',
    permissionKey: 'jobtread',
    icon: faBriefcase,
    description: 'Project management and job costing.',
    longDescription: 'JobTread is a comprehensive project management and job costing platform.',
    usage: 'LTS uses JobTread to estimate, track, and analyze project costs and timelines.',
    link: 'https://app.jobtread.com',
    color: 'bg-orange-500 text-white',
  },
  {
    title: 'Paylocity',
    permissionKey: 'paylocity',
    icon: faCreditCard,
    description: 'Payroll and HR management.',
    longDescription: 'Paylocity handles payroll, benefits, and HR processes in one platform.',
    usage: 'LTS employees use Paylocity for paystubs, benefits enrollment, and HR requests.',
    link: 'https://access.paylocity.com/?companyId=308740',
    color: 'bg-[#FF4B2B] text-white',
  },
  {
    title: 'Linxup',
    permissionKey: 'linxup',
    icon: faCar,
    description: 'Fleet tracking and vehicle management.',
    longDescription: 'Linxup provides GPS tracking and management for company vehicles.',
    usage: 'LTS uses Linxup to monitor fleet locations, optimize routes, and ensure vehicle safety.',
    link: 'https://www.linxup.com/login.html',
    color: 'bg-[#F9A825] text-black',
  },
  {
    title: 'Adobe',
    permissionKey: 'adobe',
    icon: faFilePdf,
    description: 'PDF and document tools.',
    longDescription: 'Adobe offers industry-standard tools for PDF creation, editing, and e-signatures.',
    usage: 'LTS uses Adobe for document workflows, contracts, and digital signatures.',
    link: 'https://acrobat.adobe.com/link/home/',
    color: 'bg-error text-error-content',
  },
  {
    title: 'Coast',
    permissionKey: 'coast',
    icon: faShip,
    description: 'Fuel cards and expense management.',
    longDescription: 'Coast is a platform for managing fuel cards and tracking expenses.',
    usage: 'LTS uses Coast to control fuel spending and simplify expense reporting.',
    link: 'https://portal.coastpay.com/auth',
    color: 'bg-[#0057FF] text-white',
  },
  {
    title: 'Quickbooks',
    permissionKey: 'quickbooks',
    icon: faCalculator,
    description: 'Accounting and bookkeeping.',
    longDescription: 'Quickbooks is a leading accounting software for small businesses.',
    usage: 'LTS uses Quickbooks for invoicing, payroll, and financial reporting.',
    link: 'https://app.qbo.intuit.com/',
    color: 'bg-[#22C55E] text-white',
  },
  {
    title: 'Employee Navigator',
    permissionKey: 'employeeNavigator',
    icon: faUsers,
    description: 'Benefits and HR self-service portal.',
    longDescription: 'Employee Navigator is a comprehensive benefits administration and HR self-service platform.',
    usage: 'LTS employees use Employee Navigator to manage benefits, view paystubs, and access HR information.',
    link: 'https://www.employeenavigator.com/benefits/company',
    color: 'bg-[#0066CC] text-white',
  },
  {
    title: 'CSC',
    permissionKey: 'csc',
    icon: faShieldAlt,
    description: 'Corporate compliance and governance.',
    longDescription: 'CSC provides corporate compliance, governance, and business services for companies worldwide.',
    usage: 'LTS uses CSC for corporate compliance, registered agent services, and business governance.',
    link: 'https://weblogin.cscglobal.com',
    color: 'bg-[#1E40AF] text-white',
  },
  {
    title: 'Avetta',
    permissionKey: 'avetta',
    icon: faClipboardCheck,
    description: 'Supplier prequalification and compliance.',
    longDescription: 'Avetta is a supply chain risk management platform that helps companies qualify and monitor suppliers.',
    usage: 'LTS uses Avetta to manage supplier compliance, safety qualifications, and contractor prequalification.',
    link: 'https://app.avetta.com/login',
    color: 'bg-[#059669] text-white',
  },
]

const internalAppTitles = ['Wallboard', 'Forms', 'Projects', 'Template', 'PR', 'Resources', 'Control Center', 'Tally', 'Admin', 'HR', 'Profile']
const externalAppTitles = ['JobTread', 'Paylocity', 'Linxup', 'Adobe', 'Coast', 'Quickbooks', 'Employee Navigator', 'CSC', 'Avetta']

interface DashboardPageProps {
}

function buildWallboardSearchString(searchParams: URLSearchParams): string {
  const p = new URLSearchParams(searchParams)
  p.set('homeTab', 'wallboard')
  return p.toString()
}

const DashboardPage: React.FC<DashboardPageProps> = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { showAppDescriptions } = useSettings()
  const { hasPermission } = useAuth()
  const hasAnyTallyPermission = canAccessTallyApp(hasPermission)
  const kioskRole = useKioskRole()
  const isKiosk = isKioskRole(kioskRole)
  const isDisplayKiosk = kioskRole === 'display'

  const homeTab: HomeTab = useMemo(() => {
    const raw = searchParams.get('homeTab')
    // Kiosks never show External apps.
    if (raw === 'external' && isKiosk) return 'internal'
    if (raw === 'wallboard' || raw === 'external' || raw === 'internal') {
      return raw
    }
    return 'internal'
  }, [searchParams, isKiosk])

  const setHomeTab = useCallback(
    (next: HomeTab) => {
      const p = new URLSearchParams(searchParams)
      p.set('homeTab', next)
      setSearchParams(p, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const passesPermission = (card: AppCard) =>
    card.permissionKey === null ||
    (card.title === 'Tally' ? hasAnyTallyPermission : hasPermission(card.permissionKey))

  const launchInternalApp = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, to: string) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return
      }
      event.preventDefault()
      runViewTransition(() => navigate(to))
    },
    [navigate],
  )

  const renderCard = (card: AppCard, index: number) => {
    const wallboardSearch = buildWallboardSearchString(new URLSearchParams(searchParams))

    const cardContent = (
      <div
        className={`dashboard-app-card relative card shadow-lg hover:shadow-xl ${card.color} aspect-square flex flex-col`}
        style={{ '--stagger': index } as React.CSSProperties}
      >
        <div className="flex flex-1 flex-col items-center justify-center p-4 w-full h-full">
          <FontAwesomeIcon icon={card.icon} className="text-4xl mb-2" />
          <h2 className="card-title text-center mb-2">{card.title}</h2>
          {showAppDescriptions && (
            <p className="text-center mb-4 hidden sm:block">{card.description}</p>
          )}
        </div>
      </div>
    )

    if (card.title === 'Wallboard') {
      return (
        <Link
          key={card.title}
          to={{ pathname: '/', search: wallboardSearch }}
          tabIndex={0}
          aria-label="Open Wallboard"
        >
          {cardContent}
        </Link>
      )
    }

    if (card.link.startsWith('/')) {
      return (
        <Link
          key={card.title}
          to={card.link}
          tabIndex={0}
          aria-label={`Open ${card.title}`}
          onClick={(event) => launchInternalApp(event, card.link)}
        >
          {cardContent}
        </Link>
      )
    }

    return (
      <a
        key={card.title}
        href={card.link}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={0}
        aria-label={`Open ${card.title}`}
      >
        {cardContent}
      </a>
    )
  }

  const filteredCards =
    homeTab === 'internal'
      ? appCards.filter((card) => internalAppTitles.includes(card.title) && passesPermission(card))
      : homeTab === 'external'
        ? appCards.filter((card) => externalAppTitles.includes(card.title) && passesPermission(card))
        : []

  if (isDisplayKiosk) {
    return (
      <div className="relative h-screen w-full overflow-hidden bg-[#041525]">
        <KioskLobbyAttract />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      <Navbar pageTitle="DASHBOARD" />
      <main className="container mx-auto px-4 py-8 flex-1">
        <div className="w-full">
            <div role="tablist" aria-label="Home sections" className="tabs tabs-boxed mb-6 justify-center flex-wrap gap-1">
                <button
                  type="button"
                  role="tab"
                  className={`tab duration-200 ${homeTab === 'internal' ? 'tab-active' : ''}`}
                  aria-selected={homeTab === 'internal'}
                  onClick={() => setHomeTab('internal')}
                >
                  Internal apps
                </button>
                {!isKiosk && (
                  <button
                    type="button"
                    role="tab"
                    className={`tab duration-200 ${homeTab === 'external' ? 'tab-active' : ''}`}
                    aria-selected={homeTab === 'external'}
                    onClick={() => setHomeTab('external')}
                  >
                    External apps
                  </button>
                )}
                <button
                  type="button"
                  role="tab"
                  className={`tab duration-200 ${homeTab === 'wallboard' ? 'tab-active' : ''}`}
                  aria-selected={homeTab === 'wallboard'}
                  onClick={() => setHomeTab('wallboard')}
                >
                  Wallboard
                </button>
              </div>
            <div key={homeTab} className="dashboard-tab-panel">
              {homeTab === 'wallboard' ? (
                <HomeWallboardPanel />
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(max(8rem,100%/6),1fr))] gap-4">
                  {filteredCards.map(renderCard)}
                </div>
              )}
            </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default DashboardPage 